import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { paths, listCopilotChats, getCopilotChat, getUserInstructions, updateUserInstructions, getWorkspaceInstructions, updateWorkspaceInstructions, listCopilotCsvFiles } from 'shared/api/copilot';
import type { HydratedAgUiMessage, CopilotChat, CopilotChatDetail, CopilotInstructionsDocument, UpdateUserInstructionsPayload, UpdateWorkspaceInstructionsPayload, CopilotUploadedFile } from 'shared/api/copilot';

// The raw request helpers + types live in the app-wide `shared/api/copilot`
// layer; re-export them here so the copilot feature can import its data hooks
// and request helpers from a single `shared/copilot/api` entry point.
export { paths, listCopilotChats, getCopilotChat, getUserInstructions, updateUserInstructions, getWorkspaceInstructions, updateWorkspaceInstructions };
export type { HydratedAgUiMessage, CopilotChat, CopilotChatDetail, CopilotInstructionsDocument, UpdateUserInstructionsPayload, UpdateWorkspaceInstructionsPayload };
export interface ChatHistoryEntry {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
}
export const CHAT_HISTORY_QUERY_KEY = 'copilot-chats';
export const COPILOT_CHAT_DETAIL_QUERY_KEY = 'copilot-chat-detail';
export const COPILOT_CSV_FILES_QUERY_KEY = 'copilot-csv-files';
const toCsvFilenameIndex = (files: CopilotUploadedFile[]): Readonly<Record<string, string>> => Object.fromEntries(files.map(file => [file.gcsUri, file.filename]));

/** Upserts a file into the cached CSV list so history chips resolve immediately after upload. */
export const upsertCopilotCsvFileInCache = (queryClient: QueryClient, clientId: number, userId: number | undefined, file: CopilotUploadedFile): void => {
  const queryKey = [COPILOT_CSV_FILES_QUERY_KEY, clientId, userId];
  queryClient.setQueryData<CopilotUploadedFile[]>(queryKey, existing => {
    const files = existing ?? [];
    const index = files.findIndex(entry => entry.gcsUri === file.gcsUri);
    if (index >= 0) {
      return files.map((entry, entryIndex) => entryIndex === index ? file : entry);
    }
    return [...files, file];
  });
};
const toChatHistoryEntry = (chat: CopilotChat): ChatHistoryEntry => ({
  id: chat.id,
  title: chat.title,
  createdAt: new Date(chat.created_at),
  updatedAt: new Date(chat.updated_at)
});

/**
 * Fetches the chat-history list for a client.
 *
 * The active `clientId` and `userId` are passed in by the caller (which owns the
 * client/auth context) so this hook stays free of context dependencies.
 */
export const useGetChatHistory = (clientId, userId, options) => {
  const $ = _c(16);
  const isEnabled = (options?.enabled ?? true) && Boolean(clientId) && userId != null;
  let t0;
  if ($[0] !== clientId || $[1] !== userId) {
    t0 = [CHAT_HISTORY_QUERY_KEY, clientId, userId];
    $[0] = clientId;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== clientId) {
    t1 = async () => await listCopilotChats(clientId);
    $[3] = clientId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== isEnabled || $[6] !== t0 || $[7] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      enabled: isEnabled,
      retry: false,
      throwOnError: false,
      select: _temp
    };
    $[5] = isEnabled;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data,
    isPending,
    isError,
    isFetching
  } = useQuery(t2);
  let t3;
  if ($[9] !== data) {
    t3 = data ?? [];
    $[9] = data;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== isError || $[12] !== isFetching || $[13] !== isPending || $[14] !== t3) {
    t4 = {
      data: t3,
      isPending,
      isError,
      isFetching
    };
    $[11] = isError;
    $[12] = isFetching;
    $[13] = isPending;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};

/**
 * Returns a callback that optimistically bumps a chat's `updated_at` to now in
 * the cached history list — call it when a message is created so the chat
 * immediately re-buckets to "Today" without waiting for a network refetch.
 *
 * No-ops for chats not already in the cache (e.g. a brand-new chat whose first
 * message is still in flight); those are picked up by the `onTitleGenerated`
 * refetch once the chat exists server-side.
 *
 * The active `clientId` and `userId` are passed in by the caller.
 */
export const useOptimisticallyBumpChatUpdatedAt = (clientId, userId) => {
  const $ = _c(4);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== clientId || $[1] !== queryClient || $[2] !== userId) {
    t0 = chatId => {
      const queryKey = [CHAT_HISTORY_QUERY_KEY, clientId, userId];
      const existing = queryClient.getQueryData(queryKey);
      if (existing == null) {
        return;
      }
      queryClient.setQueryData(queryKey, existing.map(chat => chat.id === chatId ? {
        ...chat,
        updated_at: new Date().toISOString()
      } : chat));
    };
    $[0] = clientId;
    $[1] = queryClient;
    $[2] = userId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};

/**
 * Fetches a single Copilot chat (with its hydrated messages) from
 * `GET /api/copilot/chats/:chatId` via react-query.
 *
 * Pass a null/undefined `chatId` to hold the request until a chat is actually
 * selected for hydration. Keyed per chat so switching between chats fetches the
 * right history; `staleTime: 0` so reopening a chat refetches fresh messages
 * rather than serving a stale snapshot.
 *
 * The active `clientId` and `userId` are passed in by the caller.
 */
export const useGetCopilotChat = (clientId, chatId, userId) => {
  const $ = _c(16);
  const enabled = chatId != null && Boolean(clientId) && userId != null;
  let t0;
  if ($[0] !== chatId || $[1] !== clientId || $[2] !== userId) {
    t0 = [COPILOT_CHAT_DETAIL_QUERY_KEY, clientId, userId, chatId];
    $[0] = chatId;
    $[1] = clientId;
    $[2] = userId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== chatId || $[5] !== clientId) {
    t1 = async () => await getCopilotChat(clientId, chatId as string);
    $[4] = chatId;
    $[5] = clientId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== enabled || $[8] !== t0 || $[9] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      enabled,
      retry: false,
      throwOnError: false,
      staleTime: 0
    };
    $[7] = enabled;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const {
    data,
    isFetching,
    isError,
    error
  } = useQuery(t2);
  let t3;
  if ($[11] !== data || $[12] !== error || $[13] !== isError || $[14] !== isFetching) {
    t3 = {
      data,
      isFetching,
      isError,
      error
    };
    $[11] = data;
    $[12] = error;
    $[13] = isError;
    $[14] = isFetching;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
export interface CopilotCsvFilenameIndexResult {
  /** `gcsUri → filename` for the user's currently-uploaded CSV files. A missing entry
   * means either the file no longer exists, or the index hasn't loaded yet — see `isLoading`. */
  filenameByGcsUri: Readonly<Record<string, string>>;
  isLoading: boolean;
  /** True once the listing request has failed — a reachability problem, not proof a file is gone. */
  isError: boolean;
}

/**
 * Fetches the user's uploaded CSV files and builds a `gcsUri → filename` index
 * for resolving attachment chip labels in message history (AG-UI content only
 * carries the URI, not the original filename), and for telling a deleted file
 * apart from one whose listing just hasn't loaded yet.
 */
export const useCopilotCsvFilenameIndex = (clientId, userId) => {
  const $ = _c(15);
  let t0;
  if ($[0] !== clientId || $[1] !== userId) {
    t0 = [COPILOT_CSV_FILES_QUERY_KEY, clientId, userId];
    $[0] = clientId;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== clientId) {
    t1 = async () => await listCopilotCsvFiles(clientId);
    $[3] = clientId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = Boolean(clientId) && userId != null;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      retry: false,
      staleTime: 300000,
      select: toCsvFilenameIndex
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data,
    isPending,
    isError
  } = useQuery(t3);
  let t4;
  if ($[9] !== data) {
    t4 = data ?? {};
    $[9] = data;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isError || $[12] !== isPending || $[13] !== t4) {
    t5 = {
      filenameByGcsUri: t4,
      isLoading: isPending,
      isError
    };
    $[11] = isError;
    $[12] = isPending;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
function _temp(chats) {
  return chats.map(toChatHistoryEntry);
}